import { ReactNode, useState } from 'react'

import { Box, Divider, Text } from '@factorialco/deprecated-design-system'
import { F0Checkbox, F0Dialog } from '@factorialco/f0-react'
import { Textarea } from '@factorialco/f0-react/dist/experimental'
import { JoinPremiumOfferPricingFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { usePricingUtils } from 'modules/ats/pages/JobPostingMarketplace/usePricingUtils'

interface Props {
  children: ReactNode
  pricing: JoinPremiumOfferPricingFragment
  isOpen: boolean
  isSubmitting?: boolean
  onClose: () => void
  onSubmit: (requesterNote: string) => void
}

export const CheckoutModal = ({
  pricing,
  isOpen,
  isSubmitting,
  onClose,
  onSubmit,
  children,
}: Props) => {
  const { formatPrice } = usePricingUtils()
  const [requesterNote, setRequesterNote] = useState('')
  const [confirmed, setConfirmed] = useState(false)

  const hasDiscount = pricing.reducedPercentage > 0
  const price = hasDiscount ? pricing.reducedAmount : pricing.originalAmount
  const totalDiscount = pricing.originalAmount - pricing.reducedAmount
  const formattedPrice = formatPrice(price, pricing.currencyCode)

  const handleSubmit = () => {
    onSubmit(requesterNote)
  }

  const handleClose = () => {
    setConfirmed(false)
    setRequesterNote('')
    onClose()
  }

  return (
    <F0Dialog
      onClose={handleClose}
      isOpen={isOpen}
      title={i18n.t('ats.job_details.marketplace.purchase_modal.title')}
      primaryAction={{
        label: i18n.t('ats.job_details.marketplace.purchase_modal.request_payment_button'),
        onClick: handleSubmit,
        loading: isSubmitting,
        disabled: !confirmed,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: handleClose,
      }}
    >
      <Box gap="s16">
        {/* Product/Bundle content */}
        {children}

        {/* Price breakdown section */}
        {hasDiscount && (
          <>
            <Divider backgroundColor="grey200" />
            <Box gap="s8">
              <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
                <Text.Normal size="300" color="grey700">
                  {i18n.t('ats.job_details.marketplace.purchase_modal.original_price')}
                </Text.Normal>
                <Text.Normal size="300" weight="semibold" color="grey700">
                  {formatPrice(pricing.originalAmount, pricing.currencyCode)}
                </Text.Normal>
              </Box>
              {
                <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
                  <Text.Normal size="300" color="grey700">
                    {i18n.t('ats.job_details.marketplace.purchase_modal.discount')}
                  </Text.Normal>
                  <Text.Normal size="300" weight="semibold" color="olive1000">
                    -{formatPrice(totalDiscount, pricing.currencyCode)}
                  </Text.Normal>
                </Box>
              }
            </Box>
          </>
        )}
        {/* Total purchase section */}
        <Box flexDirection="row" justifyContent="spaceBetween">
          <Box flexDirection="column" gap="s4">
            <Text.Normal weight="semibold" color="grey1000">
              {i18n.t('ats.job_details.marketplace.purchase_modal.total_purchase_title')}
            </Text.Normal>
            <Text.Normal size="200" color="grey700">
              {i18n.t('ats.job_details.marketplace.purchase_modal.taxes_copy')}
            </Text.Normal>
          </Box>
          <Text.Normal size="300" weight="semibold" color="grey1000">
            {formattedPrice}
          </Text.Normal>
        </Box>

        {/* Comments section */}
        <Textarea
          label={i18n.t('ats.job_details.marketplace.purchase_modal.comments_title')}
          placeholder={i18n.t('ats.job_details.marketplace.purchase_modal.comments_placeholder')}
          onChange={setRequesterNote}
          value={requesterNote}
          rows={4}
        />

        <F0Checkbox
          title={i18n.t('ats.job_details.marketplace.purchase_modal.confirmation_checkbox')}
          checked={confirmed}
          onCheckedChange={(checked) => setConfirmed(checked === true)}
        />
      </Box>
    </F0Dialog>
  )
}
